import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import {
  datetimeToJS,
  formatDateDisplay,
  formatDateUTC,
} from "@/helpers/parsers";
import { cn } from "@/lib/utils";

import type { GameType, ReviewType } from "../types";
import Details from "./details";
import ObscuredContent from "./obscured-content";
import OperatorResponse from "./operator-response";
import ReviewContent from "./review-content";

interface ReviewProps {
  className?: string;
  game: GameType;
  isFullReview?: boolean;
  path?: string[];
  review: ReviewType;
}

export default function Review({
  className,
  isFullReview = false,
  game,
  path,
  review,
}: ReviewProps) {
  const {
    escape: { gameDate },
    isContainingSpoilers,
    operatorResponse,
  } = review;

  const [isObscured, setIsObscured] = useState(isContainingSpoilers);

  const handleObscuredClick = () => setIsObscured(false);

  const dates = useMemo(() => {
    const d = [formatDateDisplay(review.createdAt)];

    if (gameDate) {
      const gameDateFormatted = formatDateUTC(datetimeToJS(gameDate));
      d.push(`Experienced ${gameDateFormatted}`);
    }

    const dates = d.join(" | ");
    const dateClassName = "text-cool-gray md:text-heather text-14/[1.2em]";

    if (isFullReview) {
      return <p className={dateClassName}>{dates}</p>;
    }

    const href = path
      ? `/${[...path.filter((s) => !s.startsWith("reviews:")), "review", review.id].join("/")}`
      : "";

    return (
      <Link
        className={cn(dateClassName, "link-overlay")}
        resetScroll={false}
        to={href}
      >
        {dates}
      </Link>
    );
  }, [gameDate, isFullReview, path, review]);

  return (
    <article
      className={cn(
        "relative rounded-[5px] border-transparent transition-colors md:border-l",
        isFullReview
          ? "py-5 md:pt-6 md:pb-0"
          : "md:p-5 hover:md:border-white/10 hover:md:bg-white/5",
        className
      )}
    >
      <div className="flex flex-col items-stretch gap-[15px]">
        <Details game={game} isFullReview={isFullReview} review={review} />
        {isObscured ? (
          <ObscuredContent onClick={handleObscuredClick} />
        ) : (
          <ReviewContent isFullReview={isFullReview} review={review} />
        )}
        {dates}
      </div>
      {!!operatorResponse && (
        <OperatorResponse
          isFullReview={isFullReview}
          response={operatorResponse}
        />
      )}
    </article>
  );
}
